import { Link, useParams, Navigate } from "react-router-dom";
import { CheckCircle2, MapPin, ChevronRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, SectionHead, FaqBlock, CtaBand, Breadcrumbs } from "@/components/Blocks";
import {
  CITIES, SERVICES, getService, serviceLd, breadcrumbLd, faqLd, comboAnchor,
} from "@/data/contentEngine";

const ServicePage = () => {
  const { serviceSlug } = useParams();
  const service = getService(serviceSlug);
  if (!service) return <Navigate to="/services" replace />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      serviceLd(service),
      breadcrumbLd([
        { label: "Home", to: "/" },
        { label: "Services", to: "/services" },
        { label: service.title, to: null },
      ].map((b) => ({ ...b, to: b.to || `/services/${service.slug}` }))),
      faqLd(service.faqs),
    ],
  };

  const related = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 4);

  return (
    <>
      <Seo
        title={`${service.title} Northern Ireland | SIA Licensed | Xtreme Protection`}
        description={`${service.short} Available in Belfast, Derry/Londonderry, Lisburn, Newry & all NI cities. Call +44 2046 402840.`}
        path={`/services/${service.slug}`}
        jsonLd={jsonLd}
      />
      <PageHero
        eyebrow="Security Service · Northern Ireland"
        title={`${service.title} Northern Ireland`}
        sub={service.short}
        image={service.image}
        testId="service-hero"
      />
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-12" data-testid="service-content">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            { label: "Services", to: "/services" },
            { label: service.title, to: null },
          ]}
        />
        <div className="mt-10 grid grid-cols-1 gap-14 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHead
              eyebrow={service.tagline}
              title={`Professional ${service.shortName} across Northern Ireland`}
              testId="service-about-heading"
            />
            <div className="mt-8 space-y-6">
              {service.paras.map((p, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <p className="text-base leading-relaxed text-slate-300">{p}</p>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.1}>
              <h2 className="mt-12 font-display text-2xl font-bold uppercase tracking-tight text-slate-100" data-testid="service-deliverables-heading">
                What the service includes
              </h2>
            </Reveal>
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {service.deliverables.map((d, i) => (
                <Reveal key={d} delay={i * 0.04}>
                  <div className="flex items-start gap-3 rounded-sm border border-slate-800 bg-obsidian-900 px-4 py-3" data-testid={`service-deliverable-${i}`}>
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                    <span className="text-sm text-slate-300">{d}</span>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.1}>
              <h2 className="mt-14 font-display text-2xl font-bold uppercase tracking-tight text-slate-100" data-testid="service-faq-heading">
                {service.title} — FAQs
              </h2>
            </Reveal>
            <div className="mt-4">
              <FaqBlock faqs={service.faqs} testId="service-faq" />
            </div>
          </div>
          <aside>
            <Reveal>
              <div className="rounded-md border border-slate-800 bg-obsidian-900 p-6" data-testid="service-cities-panel">
                <h2 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-amber-400">
                  {service.shortName} by location
                </h2>
                <p className="mt-2 text-sm text-slate-400">
                  Dedicated local coverage in every Northern Ireland city:
                </p>
                <ul className="mt-5 space-y-1.5">
                  {CITIES.map((c) => (
                    <li key={c.slug}>
                      <Link
                        to={`/services/${service.slug}/${c.slug}`}
                        data-testid={`service-city-link-${c.slug}`}
                        className="group flex items-center justify-between rounded-sm px-2 py-1.5 text-sm text-slate-300 transition-colors hover:bg-obsidian-800 hover:text-amber-400"
                      >
                        <span className="flex items-center gap-2">
                          <MapPin className="h-3.5 w-3.5 text-slate-600 group-hover:text-amber-500" />
                          {comboAnchor(service, c)}
                        </span>
                        <ChevronRight className="h-3.5 w-3.5 text-slate-700 group-hover:text-amber-500" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-6 rounded-md border border-slate-800 bg-obsidian-900 p-6" data-testid="related-services-panel">
                <h2 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-amber-400">
                  Related security services
                </h2>
                <ul className="mt-4 space-y-1.5">
                  {related.map((r) => (
                    <li key={r.slug}>
                      <Link
                        to={`/services/${r.slug}`}
                        data-testid={`related-service-${r.slug}`}
                        className="group flex items-center justify-between rounded-sm px-2 py-1.5 text-sm text-slate-300 transition-colors hover:bg-obsidian-800 hover:text-amber-400"
                      >
                        {r.title}
                        <ChevronRight className="h-3.5 w-3.5 text-slate-700 group-hover:text-amber-500" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-12">
        <CtaBand
          title={`Deploy ${service.shortName} at your premises`}
          sub="Free risk assessment and a transparent quotation from our Northern Ireland control room — urgent cover can often begin the same day."
        />
      </section>
    </>
  );
};

export default ServicePage;
